import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Image,
  Flex,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Badge,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaGift,
  FaUsers,
  FaPlus,
  FaUpload,
  FaInfoCircle,
  FaShieldAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ScambiList from '../components/ScambiCarte/ScambiList';
import AddCardModal from '../components/ScambiCarte/AddCardModal';
import BulkImportModal from '../components/ScambiCarte/BulkImportModal';
import SearchAndFilters from '../components/ScambiCarte/SearchAndFilters';
import { fetchScambi } from '../utils/scambiApi';

export default function ScambiCarte() {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  const [scambi, setScambi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attivo'); // 'cerco', 'scambio', 'attivo', 'completato'
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkImportModalOpen, setIsBulkImportModalOpen] = useState(false);
  const [isInstructionsModalOpen, setIsInstructionsModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hideOwnScambi, setHideOwnScambi] = useState(false);
  const [showOnlyOwnScambi, setShowOnlyOwnScambi] = useState(false);
  const [showOnlyWithInterested, setShowOnlyWithInterested] = useState(false);

  // Nuovi stati per la paginazione
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    pageCount: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loadingMore, setLoadingMore] = useState(false);

  // Stato per le statistiche totali
  const [statistics, setStatistics] = useState({
    totalActives: 0,
    totalCerco: 0,
    totalScambio: 0,
    totalCompleted: 0,
  });
  const [statisticsLoading, setStatisticsLoading] = useState(true);

  // Ref per controllare l'apertura dei filtri
  const openFiltersRef = useRef();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue('lg', 'xl');

  // CIL Colors + Helpers
  const cilColors = {
    yellow: '#ffcc00',
    teal: '#3eb297',
    sky: '#36a9e1',
    pink: '#e95995',
    plum: '#3b235a',
    red: '#e73341',
  };

  const API_URL = process.env.REACT_APP_STRAPI_URL || '';
  const withBase = (url) =>
    url?.startsWith('http') ? url : API_URL ? `${API_URL}${url || ''}` : url;

  const loadScambi = async (page = 1, append = false) => {
    if (page === 1) {
      setLoading(true);
      setStatisticsLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      let data;

      // Per tutti i tab, usa il filtraggio lato server per evitare problemi di paginazione
      if (activeTab === 'cerco') {
        // Per il tab cerco, filtra per tipo 'cerco' e stato non completato
        data = await fetchScambi(page, pagination.pageSize, {
          tipo: 'cerco',
          stato: ['attivo', 'in-trattativa'],
        });
      } else if (activeTab === 'scambio') {
        // Per il tab scambio, filtra per tipo 'scambio' e stato non completato
        data = await fetchScambi(page, pagination.pageSize, {
          tipo: 'scambio',
          stato: ['attivo', 'in-trattativa'],
        });
      } else if (activeTab === 'attivo') {
        // Per il tab attivo, filtra per stato attivo o in-trattativa
        data = await fetchScambi(page, pagination.pageSize, {
          stato: ['attivo', 'in-trattativa'],
        });
      } else if (activeTab === 'completato') {
        // Per il tab completato, filtra per stato completato
        data = await fetchScambi(page, pagination.pageSize, {
          stato: ['completato'],
        });
      } else {
        // Fallback: carica tutti gli scambi
        data = await fetchScambi(page, pagination.pageSize);
      }

      if (append && page > 1) {
        // Aggiungi i nuovi scambi a quelli esistenti
        setScambi((prev) => [...prev, ...data.scambi]);
      } else {
        // Sostituisci completamente gli scambi
        setScambi(data.scambi);
      }

      // Aggiorna la paginazione
      setPagination(data.pagination);

      // Aggiorna le statistiche totali se disponibili
      if (data.statistics) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      toast({
        title: 'Errore nel caricamento',
        description: 'Impossibile caricare gli scambi',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
      if (page === 1) {
        setStatisticsLoading(false);
      }
    }
  };

  // Funzione per caricare più scambi
  const loadMoreScambi = () => {
    if (pagination.hasNextPage && !loadingMore) {
      loadScambi(pagination.page + 1, true);
    }
  };

  // Funzione wrapper per onCardUpdated che resetta la paginazione
  const handleCardUpdated = (closeModal = false, showToast = true) => {
    if (closeModal) {
      setIsAddModalOpen(false);
    }
    setPagination((prev) => ({ ...prev, page: 1 }));
    setStatisticsLoading(true); // Reset statistics loading state
    loadScambi(1, false);
    if (showToast) {
      toast({
        title: 'Carta aggiornata',
        description: 'La carta è stata aggiornata con successo',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    if (userData) {
      loadScambi();
    }
  }, [userData]);

  // Reset paginazione quando cambia il tab
  useEffect(() => {
    if (userData) {
      setPagination((prev) => ({ ...prev, page: 1 }));
      setScambi([]); // Pulisce gli scambi esistenti
      setStatisticsLoading(true); // Reset statistics loading state
      loadScambi(1, false);
    }
  }, [activeTab, userData]);

  // Notifica per scambi con soci interessati
  useEffect(() => {
    if (userData && scambi.length > 0) {
      const scambiConSociInteressati = scambi.filter((scambio) => {
        const isOwnScambio =
          scambio.user?.data?.id === userData.id ||
          scambio.user?.id === userData.id ||
          scambio.userId === userData.id ||
          scambio.owner === userData.id ||
          scambio.createdBy === userData.id;
        // Escludi gli scambi completati
        const isCompleted = scambio.stato === 'completato';
        return (
          isOwnScambio &&
          !isCompleted &&
          scambio.tags &&
          scambio.tags.filter(
            (tag) => tag.startsWith('ce-lho_') || tag.startsWith('mi-serve_')
          ).length > 0
        );
      });

      if (scambiConSociInteressati.length > 0) {
        toast({
          title: `🎉 Hai ${scambiConSociInteressati.length} scambi con soci interessati!`,
          description: 'Clicca per vedere solo questi scambi',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
          variant: 'solid',
          render: ({ onClose }) => (
            <Box
              bg="green.500"
              color="white"
              p={4}
              borderRadius="md"
              cursor="pointer"
              onClick={() => {
                setShowOnlyWithInterested(true);
                setShowOnlyOwnScambi(false);
                setHideOwnScambi(false);
                // Apre anche il dropdown dei filtri
                if (
                  openFiltersRef.current &&
                  openFiltersRef.current.openFilters
                ) {
                  openFiltersRef.current.openFilters();
                }
                onClose();
              }}
              _hover={{ bg: 'green.600' }}
              transition="background 0.2s"
            >
              <Text fontWeight="bold" fontSize="md">
                🎉 Hai {scambiConSociInteressati.length} scambi con soci
                interessati!
              </Text>
              <Text fontSize="sm" opacity={0.9}>
                Clicca per vedere solo questi scambi
              </Text>
            </Box>
          ),
        });
      }
    }
  }, [scambi, userData, toast]);

  const filteredScambi = scambi.filter((scambio) => {
    // I dati sono già filtrati per tab dal server, quindi qui filtriamo solo per:
    // - Filtri utente (propri scambi, nascondi propri scambi)
    // - Filtri di interesse (soci interessati)
    // - Ricerca testuale

    // Filtra i propri scambi se richiesto
    if (hideOwnScambi && userData) {
      const isOwnScambio =
        scambio.user?.data?.id === userData.id ||
        scambio.user?.id === userData.id ||
        scambio.userId === userData.id ||
        scambio.owner === userData.id ||
        scambio.createdBy === userData.id;
      if (isOwnScambio) return false;
    }

    // Mostra solo i propri scambi se richiesto
    if (showOnlyOwnScambi && userData) {
      const isOwnScambio =
        scambio.user?.data?.id === userData.id ||
        scambio.user?.id === userData.id ||
        scambio.userId === userData.id ||
        scambio.owner === userData.id ||
        scambio.createdBy === userData.id;
      if (!isOwnScambio) return false;
    }

    // Mostra solo scambi con soci interessati se richiesto
    if (showOnlyWithInterested) {
      const hasInterestedSoci =
        scambio.tags &&
        scambio.tags.filter(
          (tag) => tag.startsWith('ce-lho_') || tag.startsWith('mi-serve_')
        ).length > 0;
      if (!hasInterestedSoci) return false;
    }

    // Filtra per query di ricerca (se presente)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const cardName = scambio.carte?.nome?.toLowerCase() || '';
      const note = scambio.note?.toLowerCase() || '';
      const userName = scambio.user?.data?.username?.toLowerCase() || '';

      return (
        cardName.includes(query) ||
        note.includes(query) ||
        userName.includes(query)
      );
    }

    return true;
  });

  // Se l'utente non è loggato, mostra solo la hero con bottone "Diventa Socio"
  if (!userData) {
    return (
      <Box>
        {/* HERO SECTION - Solo per utenti non loggati */}
        <Box
          position="relative"
          w="full"
          h="70vh"
          minH="500px"
          overflow="hidden"
          mb={0}
        >
          <Image
            src={
              withBase(
                'https://cms.cilassociazione.it/uploads/carnevale_munlab_d7caee7803.jpg'
              ) || '/fallback-hero.jpg'
            }
            alt="Scambi Carte Magic - Connetti con altri soci CIL"
            w="full"
            h="full"
            objectFit="cover"
          />

          {/* Overlay scuro per migliorare la leggibilità */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
          />

          {/* Contenuto centrato con messaggio di tesseramento */}
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            align="center"
            justify="center"
            direction="column"
            textAlign="center"
            px={6}
          >
            <Heading
              size="4xl"
              color="white"
              mb={6}
              textShadow="2px 2px 4px rgba(0,0,0,0.8)"
              fontWeight="extrabold"
              letterSpacing="wide"
              maxW="800px"
            >
              🃏 Scambi Carte Magic
            </Heading>

            <Text
              fontSize="xl"
              color="white"
              maxW="600px"
              mb={8}
              textShadow="1px 1px 2px rgba(0,0,0,0.8)"
              fontWeight="medium"
              lineHeight="1.7"
            >
              Per accedere alla piattaforma di scambi carte Magic: The Gathering
              e connetterti con altri soci CIL, devi prima diventare socio
              dell&apos;associazione.
            </Text>

            {/* CTA per tesseramento */}
            <Button
              size="xl"
              bg={cilColors.yellow}
              color={cilColors.plum}
              _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
              px={12}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              leftIcon={<FaUsers />}
              transition="all 0.3s"
              onClick={() => navigate('/login')}
            >
              Entra in CIL
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg="transparent" minH="100vh" overflowX="hidden" p={4}>
      <Container maxW="container.xl" py={8}>
        {/* Header */}
        <Box
          bg="whiteAlpha.800"
          boxShadow="md"
          borderRadius="lg"
          px={8}
          py={8}
          mb={6}
          textAlign="center"
        >
          <VStack spacing={4}>
            <Heading
              as="h1"
              size="xl"
              color="blue.600"
              fontFamily="Poppins, sans-serif"
            >
              🃏 Scambi Carte Magic
            </Heading>

            {/* Istruzioni e Disclaimer */}
            <VStack spacing={4} maxW="4xl">
              <Text
                fontSize="md"
                color="gray.700"
                fontFamily="Montserrat, sans-serif"
                lineHeight="1.6"
              >
                Benvenuto nella piattaforma di scambi carte Magic: The
                Gathering! Qui puoi pubblicare le carte che cerchi o che vuoi
                scambiare, connetterti con altri soci CIL e gestire i tuoi
                scambi in modo semplice e sicuro.
              </Text>

              <HStack spacing={4} justify="center" wrap="wrap">
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<FaInfoCircle />}
                  onClick={() => setIsInstructionsModalOpen(true)}
                  _hover={{ bg: 'blue.50' }}
                >
                  Leggi le Istruzioni
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  leftIcon={<FaShieldAlt />}
                  onClick={() => setIsPrivacyModalOpen(true)}
                  _hover={{ bg: 'orange.50' }}
                >
                  Privacy & Sicurezza
                </Button>
              </HStack>
            </VStack>
          </VStack>
        </Box>

        {/* Navigation Tabs */}
        <Box bg={cardBg} boxShadow={cardShadow} borderRadius="lg" p={3} mb={4}>
          <HStack spacing={3} justify="center" wrap="wrap">
            <Button
              variant={activeTab === 'attivo' ? 'solid' : 'outline'}
              colorScheme="green"
              leftIcon={<FaUsers />}
              onClick={() => setActiveTab('attivo')}
              size="md"
            >
              Attivi
              <Badge ml={2} colorScheme="green" variant="solid" fontSize="xs">
                {statisticsLoading ? '...' : statistics.totalActives}
              </Badge>
            </Button>
            <Button
              variant={activeTab === 'cerco' ? 'solid' : 'outline'}
              colorScheme="green"
              leftIcon={<FaSearch />}
              onClick={() => setActiveTab('cerco')}
              size="md"
            >
              Cerco Carte
              <Badge ml={2} colorScheme="green" variant="solid" fontSize="xs">
                {statisticsLoading ? '...' : statistics.totalCerco}
              </Badge>
            </Button>
            <Button
              variant={activeTab === 'scambio' ? 'solid' : 'outline'}
              colorScheme="purple"
              leftIcon={<FaGift />}
              onClick={() => setActiveTab('scambio')}
              size="md"
            >
              Scambio Carte
              <Badge ml={2} colorScheme="purple" variant="solid" fontSize="xs">
                {statisticsLoading ? '...' : statistics.totalScambio}
              </Badge>
            </Button>
            <Button
              variant={activeTab === 'completato' ? 'solid' : 'outline'}
              colorScheme="blue"
              leftIcon={<FaUsers />}
              onClick={() => setActiveTab('completato')}
              size="md"
            >
              Completati
              <Badge ml={2} colorScheme="blue" variant="solid" fontSize="xs">
                {statisticsLoading ? '...' : statistics.totalCompleted}
              </Badge>
            </Button>
          </HStack>
        </Box>

        {/* Search and Filters Component */}
        <SearchAndFilters
          ref={openFiltersRef}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          hideOwnScambi={hideOwnScambi}
          setHideOwnScambi={setHideOwnScambi}
          showOnlyOwnScambi={showOnlyOwnScambi}
          setShowOnlyOwnScambi={setShowOnlyOwnScambi}
          showOnlyWithInterested={showOnlyWithInterested}
          setShowOnlyWithInterested={setShowOnlyWithInterested}
        />

        {/* Add Card Button and Notification */}
        <Box textAlign="center" mb={6}>
          <VStack spacing={4} display={{ base: 'flex', md: 'none' }}>
            <Button
              size="lg"
              colorScheme="yellow"
              bg="#ffcc00"
              color="#3b235a"
              leftIcon={<FaPlus />}
              onClick={() => setIsAddModalOpen(true)}
              _hover={{
                bg: '#e6b800',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              _active={{ bg: '#d4a600' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              w="100%"
              maxW="300px"
            >
              Aggiungi Carta
            </Button>

            <Button
              size="lg"
              colorScheme="blue"
              bg="#3182ce"
              color="white"
              leftIcon={<FaUpload />}
              onClick={() => setIsBulkImportModalOpen(true)}
              _hover={{
                bg: '#2c5aa0',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(49, 130, 206, 0.3)',
              }}
              _active={{ bg: '#2a4365' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              w="100%"
              maxW="300px"
            >
              Importa Bulk
            </Button>
          </VStack>

          {/* Desktop Layout */}
          <HStack
            spacing={6}
            justify="center"
            align="center"
            display={{ base: 'none', md: 'flex' }}
          >
            <Button
              size="lg"
              colorScheme="yellow"
              bg="#ffcc00"
              color="#3b235a"
              leftIcon={<FaPlus />}
              onClick={() => setIsAddModalOpen(true)}
              _hover={{
                bg: '#e6b800',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              _active={{ bg: '#d4a600' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
            >
              Aggiungi Carta
            </Button>

            <Button
              size="lg"
              colorScheme="blue"
              bg="#3182ce"
              color="white"
              leftIcon={<FaUpload />}
              onClick={() => setIsBulkImportModalOpen(true)}
              _hover={{
                bg: '#2c5aa0',
                transform: 'scale(1.05)',
                boxShadow: '0 8px 25px rgba(49, 130, 206, 0.3)',
              }}
              _active={{ bg: '#2a4365' }}
              transition="all 0.3s ease"
              px={8}
              py={6}
              fontWeight="bold"
              fontSize="lg"
            >
              Importa Bulk
            </Button>
          </HStack>
        </Box>

        {/* Content */}
        {loading ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="blue.500" />
              <Text color="gray.600">Caricamento scambi...</Text>
            </VStack>
          </Center>
        ) : (
          <>
            <ScambiList
              scambi={filteredScambi}
              activeTab={activeTab}
              userData={userData}
              onCardUpdated={() => handleCardUpdated(false, false)}
            />

            {/* Pulsante per caricare più scambi */}
            {pagination.hasNextPage && (
              <Box textAlign="center" mt={8}>
                <Button
                  size="lg"
                  colorScheme="blue"
                  variant="outline"
                  onClick={loadMoreScambi}
                  isLoading={loadingMore}
                  loadingText="Caricamento..."
                  leftIcon={<FaPlus />}
                  transition="all 0.2s"
                  borderColor="blue.500"
                  color="blue.600"
                  _hover={{
                    bg: 'blue.50',
                    transform: 'scale(1.02)',
                    borderColor: 'blue.600',
                  }}
                >
                  Carica Altri Scambi
                </Button>
                <Text fontSize="sm" color="gray.700" mt={2} fontWeight="medium">
                  Mostrando {scambi.length} di {pagination.total} scambi totali
                </Text>
              </Box>
            )}
          </>
        )}

        {/* Add Card Modal */}
        <AddCardModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onCardAdded={() => handleCardUpdated(true)}
          userData={userData}
        />

        {/* Instructions Modal */}
        <Modal
          isOpen={isInstructionsModalOpen}
          onClose={() => setIsInstructionsModalOpen(false)}
          size="xl"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack spacing={3}>
                <Icon as={FaInfoCircle} color="blue.500" />
                <Text>📖 Istruzioni per gli Scambi</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" color="blue.600" mb={3}>
                    Come Funziona
                  </Heading>
                  <VStack spacing={3} align="stretch">
                    <Box
                      p={4}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      <Text fontWeight="semibold" color="blue.700">
                        1. 📥 Cerco Carte
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Pubblica le carte che stai cercando. Altri soci potranno
                        vedere la tua richiesta e contattarti se hanno la carta.
                      </Text>
                    </Box>

                    <Box
                      p={4}
                      bg="purple.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="purple.200"
                    >
                      <Text fontWeight="semibold" color="purple.700">
                        2. 🎁 Scambio Carte
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Pubblica le carte che vuoi scambiare. I soci interessati
                        potranno contattarti per proporre uno scambio.
                      </Text>
                    </Box>

                    <Box
                      p={4}
                      bg="green.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Text fontWeight="semibold" color="green.700">
                        3. 🤝 Gestione Scambi
                      </Text>
                      <Text fontSize="sm" color="gray.700" mt={1}>
                        Quando qualcuno è interessato, usa i pulsanti &ldquo;Ce
                        L&apos;ho!&rdquo; o &ldquo;Mi serve!&rdquo; per
                        registrare l&apos;interesse. I contatti verranno
                        mostrati per facilitare la comunicazione.
                      </Text>
                    </Box>
                  </VStack>
                </Box>

                <Box>
                  <Heading size="md" color="green.600" mb={3}>
                    Suggerimenti
                  </Heading>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • Inserisci sempre note dettagliate sulle condizioni della
                      carta
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Specifica la lingua e l&apos;edizione della carta
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Aggiorna lo stato dei tuoi scambi quando necessario
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Contatta i soci interessati per finalizzare gli scambi
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={() => setIsInstructionsModalOpen(false)}
              >
                Ho Capito
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Privacy Modal */}
        <Modal
          isOpen={isPrivacyModalOpen}
          onClose={() => setIsPrivacyModalOpen(false)}
          size="lg"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack spacing={3}>
                <Icon as={FaShieldAlt} color="orange.500" />
                <Text>🔒 Privacy & Sicurezza</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />

            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Box
                  p={4}
                  bg="orange.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="orange.200"
                >
                  <Text fontWeight="semibold" color="orange.700" mb={2}>
                    Informazioni Visibili
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Nome utente:</strong> Visibile a tutti i soci
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Telefono:</strong> Visibile ai soci interessati
                      ai tuoi scambi
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • <strong>Email:</strong> Non visibile (utilizzata solo
                      per notifiche)
                    </Text>
                  </VStack>
                </Box>

                <Box
                  p={4}
                  bg="blue.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontWeight="semibold" color="blue.700" mb={2}>
                    Come Proteggiamo i Tuoi Dati
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • I dati sono accessibili solo ai soci CIL registrati
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • I contatti vengono mostrati solo quando necessario per
                      gli scambi
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Puoi sempre modificare o rimuovere i tuoi scambi
                    </Text>
                  </VStack>
                </Box>

                <Box
                  p={4}
                  bg="green.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="green.200"
                >
                  <Text fontWeight="semibold" color="green.700" mb={2}>
                    Raccomandazioni
                  </Text>
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="sm" color="gray.700">
                      • Usa solo il telefono per contatti diretti
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Non condividere informazioni personali sensibili
                    </Text>
                    <Text fontSize="sm" color="gray.700">
                      • Contatta gli amministratori se hai dubbi sulla privacy
                    </Text>
                  </VStack>
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button
                colorScheme="orange"
                onClick={() => setIsPrivacyModalOpen(false)}
              >
                Ho Capito
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Bulk Import Modal */}
        <BulkImportModal
          isOpen={isBulkImportModalOpen}
          onClose={() => setIsBulkImportModalOpen(false)}
          onCardsAdded={() => handleCardUpdated(true)}
          userData={userData}
        />
      </Container>
    </Box>
  );
}
